// The day's weather over the home in view: gentle rain (or, in winter, snow
// flurries) when the world says so (live.energy.sky, a pure function of the
// Plazmi day on the server). Rain comes in showers, on and off: streaks fall
// over the lot, drops splash on the lawn and ring the puddles, the grass
// darkens and takes a sheen, and the sky goes a soft grey. Snow drifts down
// in slow flakes. All of it is a handful of draw calls: one set of streaks or
// flakes, one of splashes, one of puddles.
//   ?weather=rain | snow | clear holds it, for a look or a screenshot.
import * as THREE from 'three';
import { scaled } from './quality.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { rng } from './util.js';
import type { Daylight } from './daylight.js';

export type Sky = 'rain' | 'snow' | null;

// what the live JSON says, as rain, snow or nothing
export function skyOf(live: any, override: string | null): Sky {
  if (override === 'rain' || override === 'snow') return override;
  if (override === 'clear' || override === 'sun') return null;
  const e = live?.energy;
  if (!e || e.sky !== 'rain') return null;
  return (live?.sky?.season || e.season) === 'winter' || /snow/i.test(e.weather || '') ? 'snow' : 'rain';
}

// how many, by the graphics tier (quality.ts), read when the rain or snow begins
const counts = () => ({ N_RAIN: scaled(1400, 'weather', 120), N_SNOW: scaled(900, 'weather', 80), N_SPLASH: scaled(90, 'weather', 12) });
const WHITE = new THREE.Color(1, 1, 1);
const tmpV = new THREE.Vector3();

function streaks(W: number, D: number, top: number) {
  const { N_RAIN } = counts();
  const r = rng(91);
  const pos = new Float32Array(N_RAIN * 2 * 3), seed = new Float32Array(N_RAIN * 2 * 2);
  for (let i = 0; i < N_RAIN; i++) {
    const x = -2 + r() * (W + 4), z = -2 + r() * (D + 4), s = r(), k = r();
    for (let e = 0; e < 2; e++) { pos.set([x, e, z], (i * 2 + e) * 3); seed.set([s, k], (i * 2 + e) * 2); }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('seed', new THREE.BufferAttribute(seed, 2));
  g.boundingSphere = new THREE.Sphere(new THREE.Vector3(W / 2, top / 2, D / 2), Math.hypot(W, D, top));
  const m = new THREE.ShaderMaterial({
    uniforms: { time: { value: 0 }, amount: { value: 0 }, top: { value: top }, wind: { value: new THREE.Vector2(0.18, 0.06) }, tint: { value: new THREE.Color(0.82, 0.88, 0.96) } },
    vertexShader: `
      attribute vec2 seed; uniform float time; uniform float top; uniform vec2 wind;
      varying float vEnd; varying float vSeed;
      void main(){
        float speed = 11.0 + seed.y * 4.0;
        float y = top - mod(time * speed + seed.x * top, top);
        float len = 0.42 + seed.y * 0.3;
        vec3 p = vec3(position.x, y + position.y * len, position.z);
        p.xz += wind * (top - y) * 0.12 + wind * position.y * len * 0.6;
        vEnd = position.y; vSeed = seed.x;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
      }`,
    fragmentShader: `
      uniform float amount; uniform vec3 tint; varying float vEnd; varying float vSeed;
      void main(){
        if (vSeed > amount) discard;
        gl_FragColor = vec4(tint, 0.28 * vEnd + 0.03);
      }`,
    transparent: true, depthWrite: false,
  });
  const l = new THREE.LineSegments(g, m);
  l.frustumCulled = false;
  return l;
}

function flakes(W: number, D: number, top: number) {
  const { N_SNOW } = counts();
  const r = rng(57);
  const pos = new Float32Array(N_SNOW * 3), seed = new Float32Array(N_SNOW * 2);
  for (let i = 0; i < N_SNOW; i++) { pos.set([-2 + r() * (W + 4), 0, -2 + r() * (D + 4)], i * 3); seed.set([r(), r()], i * 2); }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('seed', new THREE.BufferAttribute(seed, 2));
  const m = new THREE.ShaderMaterial({
    uniforms: { time: { value: 0 }, amount: { value: 0 }, top: { value: top }, scale: { value: 400 } },
    vertexShader: `
      attribute vec2 seed; uniform float time; uniform float top; uniform float scale;
      varying float vSeed;
      void main(){
        float speed = 0.7 + seed.y * 0.6;
        float y = top - mod(time * speed + seed.x * top, top);
        vec3 p = position + vec3(sin(time * 0.8 + seed.x * 30.0) * 0.45, y, cos(time * 0.6 + seed.y * 30.0) * 0.35);
        vec4 mv = modelViewMatrix * vec4(p, 1.0);
        vSeed = seed.x;
        gl_PointSize = scale * (0.07 + seed.y * 0.05) / -mv.z;
        gl_Position = projectionMatrix * mv;
      }`,
    fragmentShader: `
      uniform float amount; varying float vSeed;
      void main(){
        if (vSeed > amount) discard;
        float r = length(gl_PointCoord - 0.5) * 2.0;
        float a = smoothstep(1.0, 0.35, r) * 0.9;
        if (a < 0.01) discard;
        gl_FragColor = vec4(vec3(1.0), a);
      }`,
    transparent: true, depthWrite: false,
  });
  const p = new THREE.Points(g, m);
  p.frustumCulled = false;
  return p;
}

// little rings where drops land: on the lawn, and more of them on the puddles
function splashes(spots: [number, number][]) {
  const { N_SPLASH } = counts();
  const pos = new Float32Array(N_SPLASH * 3), seed = new Float32Array(N_SPLASH);
  const r = rng(13);
  for (let i = 0; i < N_SPLASH; i++) {
    const [x, z] = spots[i % spots.length];
    pos.set([x + (r() - 0.5) * 0.5, 0.06, z + (r() - 0.5) * 0.5], i * 3); seed[i] = r();
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('seed', new THREE.BufferAttribute(seed, 1));
  const m = new THREE.ShaderMaterial({
    uniforms: { time: { value: 0 }, amount: { value: 0 }, scale: { value: 400 }, tint: { value: new THREE.Color(0.86, 0.92, 1.0) } },
    vertexShader: `
      attribute float seed; uniform float time; uniform float scale;
      varying float vPh; varying float vSeed;
      void main(){
        vPh = fract(time * (1.1 + seed * 0.7) + seed * 9.0);
        vSeed = seed;
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = scale * (0.08 + 0.24 * vPh) / -mv.z;
        gl_Position = projectionMatrix * mv;
      }`,
    fragmentShader: `
      uniform float amount; uniform vec3 tint; varying float vPh; varying float vSeed;
      void main(){
        if (vSeed > amount) discard;
        vec2 d = (gl_PointCoord - 0.5) * vec2(2.0, 3.6); // a ring lying on the ground, seen from above at a slant
        float r = length(d);
        float ring = smoothstep(0.16, 0.0, abs(r - 0.82)) * (1.0 - vPh);
        if (ring < 0.02) discard;
        gl_FragColor = vec4(tint, ring * 0.42);
      }`,
    transparent: true, depthWrite: false,
  });
  const p = new THREE.Points(g, m);
  p.frustumCulled = false;
  return p;
}

export class Weather {
  root = new THREE.Group();
  sky: Sky = null;
  wet = 0;            // how wet it is now (eases toward aim)
  private aim = 0;
  private t = 0;
  private rain: THREE.LineSegments | null = null;
  private snow: THREE.Points | null = null;
  private splash: THREE.Points | null = null;
  private puddles: THREE.Mesh | null = null;
  private puddleMat = new THREE.MeshStandardMaterial({ color: '#ffffff', vertexColors: true, roughness: 0.06, metalness: 0.15, envMapIntensity: 1.6, transparent: true, opacity: 0, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 });
  private lawns: { m: THREE.MeshStandardMaterial; color: THREE.Color; rough: number }[] = [];
  private W = 12; private D = 13;
  private applied = -1;
  private tint = new THREE.Color(0.82, 0.88, 0.96);

  constructor(private daylight: Daylight, private renderer: THREE.WebGLRenderer, private cam: THREE.PerspectiveCamera) {
    this.root.name = 'weather';
    this.root.userData.noAO = true; this.root.userData.noShadow = true;
  }

  // a new lot: its lawn to wet, and puddles where the ground is open
  setLot(lot: THREE.Object3D, house: { plot: [number, number]; spec: any; yard: { at: [number, number] }[] }, door: [number, number] | null) {
    this.restoreLawn();
    this.lawns = [];
    lot.traverse((o: any) => {
      if (!o.isMesh) return;
      for (const m of Array.isArray(o.material) ? o.material : [o.material]) {
        if (m?.userData?.lawn && !this.lawns.some((l) => l.m === m)) this.lawns.push({ m, color: m.color.clone(), rough: m.roughness });
      }
    });
    const [W, D] = house.plot;
    this.W = W; this.D = D;
    for (const o of [this.rain, this.snow, this.splash, this.puddles]) if (o) { this.root.remove(o); o.geometry.dispose(); if (o !== this.puddles) (o.material as THREE.Material).dispose(); }
    this.rain = this.snow = this.splash = this.puddles = null;
    // open ground: off the house, away from things in the garden and the path, in from the edge
    const ms = (house.spec?.masses || []).filter((m: any) => !(m.level > 0));
    const open = (x: number, z: number, pad: number) => x > 1.2 && x < W - 1.2 && z > 1.0 && z < D - 0.9
      && !ms.some((m: any) => x > m.x - pad - 0.6 && x < m.x + m.w + pad + 0.6 && z > m.y - pad - 0.6 && z < m.y + m.d + pad + 1.4)
      && !house.yard.some((t) => Math.hypot(t.at[0] - x, t.at[1] - z) < 1.3 + pad)
      && !(door && Math.abs(x - door[0]) < 1.0 + pad && z > door[1]);
    const r = rng((W * 31 + D * 7) | 0);
    const pools: [number, number, number, number][] = [];
    for (let k = 0; k < 160 && pools.length < 4; k++) {
      const x = 1 + r() * (W - 2), z = 1 + r() * (D - 2);
      const a = 0.45 + r() * 0.45, b = a * (0.55 + r() * 0.3);
      if (!open(x, z, a) || pools.some(([px, pz]) => Math.hypot(px - x, pz - z) < 2.6)) continue;
      pools.push([x, z, a, b]);
    }
    if (pools.length) {
      const gs = pools.map(([x, z, a, b]) => {
        const g = new THREE.CircleGeometry(1, 20);
        // a little wobble round the edge, so it reads as water lying on grass
        const p = g.attributes.position;
        for (let i = 1; i < p.count; i++) { const k = 1 + Math.sin(i * 2.3 + x) * 0.08 + Math.sin(i * 0.9 + z) * 0.06; p.setXY(i, p.getX(i) * k, p.getY(i) * k); }
        // the sky in the middle, a darker wet edge
        const cs = new Float32Array(p.count * 3), mid = new THREE.Color('#a9b8c6'), edge = new THREE.Color('#5d6974');
        for (let i = 0; i < p.count; i++) (i === 0 ? mid : edge).toArray(cs, i * 3);
        g.setAttribute('color', new THREE.BufferAttribute(cs, 3));
        g.scale(a, b, 1); g.rotateX(-Math.PI / 2); g.rotateY(r() * Math.PI); g.translate(x, 0.022, z);
        return g;
      });
      this.puddles = new THREE.Mesh(mergeGeometries(gs), this.puddleMat);
      this.puddles.receiveShadow = true;
      this.puddles.renderOrder = 1;
      for (const g of gs) g.dispose();
      this.root.add(this.puddles);
    }
    // splashes: half on the puddles, half across the open lawn
    const spots: [number, number][] = [];
    for (const [x, z] of pools) spots.push([x, z], [x, z]);
    for (let k = 0; k < 200 && spots.length < 24; k++) { const x = 1 + r() * (W - 2), z = 1 + r() * (D - 2); if (open(x, z, 0)) spots.push([x, z]); }
    if (spots.length) { this.splash = splashes(spots); this.root.add(this.splash); }
    this.build();
  }

  private build() {
    const top = 15;
    if (this.sky === 'rain' && !this.rain) { this.rain = streaks(this.W, this.D, top); this.root.add(this.rain); }
    if (this.sky === 'snow' && !this.snow) { this.snow = flakes(this.W, this.D, top); this.root.add(this.snow); }
  }

  set(sky: Sky) {
    if (sky === this.sky) return;
    // a change of weather lets the old one fade out first
    if (sky && this.sky && sky !== this.sky) { this.aim = 0; this.next = sky; return; }
    this.sky = sky ?? this.sky;
    this.aim = sky ? 1 : 0;
    if (sky) this.build();
  }
  private next: Sky = null;

  // how much is falling: rain comes in showers, heavier and lighter over a few minutes
  private amountNow() {
    const s = Math.sin(this.t / 47) * 0.5 + Math.sin(this.t / 113 + 1.3) * 0.5;
    return this.wet * (0.62 + 0.38 * s);
  }

  update(dt: number) {
    this.t += dt;
    if (this.wet !== this.aim) {
      const step = dt / 6; // about six seconds to come on, or clear
      this.wet = this.aim > this.wet ? Math.min(this.aim, this.wet + step) : Math.max(this.aim, this.wet - step);
      if (this.wet === 0 && this.next) { this.sky = this.next; this.next = null; this.aim = 1; this.build(); }
      else if (this.wet === 0) this.sky = null;
    }
    const w = this.wet, snowing = this.sky === 'snow';
    // the sky greys over; the lawn darkens and shines (rain only: snow keeps it matte)
    const k = Math.round(w * 200) / 200;
    if (k !== this.applied) {
      this.applied = k;
      this.daylight.wet = snowing ? w * 0.6 : w;
      this.daylight.apply();
      for (const l of this.lawns) {
        const wetK = snowing ? 0 : w;
        l.m.color.copy(l.color).multiplyScalar(1 - 0.3 * wetK);
        l.m.roughness = l.rough - (l.rough - 0.42) * wetK;
      }
      this.puddleMat.opacity = snowing ? 0 : 0.82 * w;
      this.puddleMat.visible = !snowing && w > 0.01;
    }
    this.root.visible = w > 0.005;
    if (!this.root.visible) return;
    // stepped back to see the neighbours, the shower thins out (a box of rain from afar reads as a block)
    const far = this.cam.position.distanceTo(tmpV.set(this.W / 2, 1, this.D / 2)), near = Math.max(this.W, this.D) * 4.2;
    const amount = this.amountNow() * Math.min(1, Math.max(0, 1 - (far - near) / near));
    // the rain catches what light there is: pale by day, a dim blue after dark
    const fog = (this.root.parent as THREE.Scene | null)?.fog as THREE.Fog | undefined;
    if (fog) this.tint.copy(fog.color).lerp(WHITE, 0.45).multiplyScalar(1.05);
    const scale = this.renderer.getDrawingBufferSize(new THREE.Vector2()).y / (2 * Math.tan(THREE.MathUtils.degToRad(this.cam.fov / 2)));
    if (this.rain) { const u = (this.rain.material as THREE.ShaderMaterial).uniforms; u.time.value = this.t; u.tint.value.copy(this.tint); u.amount.value = this.sky === 'rain' ? amount : 0; this.rain.visible = this.sky === 'rain'; }
    if (this.snow) { const u = (this.snow.material as THREE.ShaderMaterial).uniforms; u.time.value = this.t; u.amount.value = snowing ? amount : 0; u.scale.value = scale; this.snow.visible = snowing; }
    if (this.splash) { const u = (this.splash.material as THREE.ShaderMaterial).uniforms; u.time.value = this.t; u.tint.value.copy(this.tint); u.amount.value = snowing ? 0 : amount * 0.9; u.scale.value = scale; this.splash.visible = !snowing; }
  }

  private restoreLawn() { for (const l of this.lawns) { l.m.color.copy(l.color); l.m.roughness = l.rough; } }

  dispose() {
    this.restoreLawn();
    this.daylight.wet = 0; this.daylight.apply();
    this.root.removeFromParent();
    this.root.traverse((o: any) => { o.geometry?.dispose(); });
  }
}
